import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AI_FOLLOWUP_DEFAULT_PROMPT,
  AI_FOLLOWUP_TIMEZONES,
  AiFollowupSettingInputSchema,
  type AiFollowupSettingInput,
} from '@app/shared';
import {
  useAiFollowupSetting,
  useRunAiFollowupNow,
  useUpdateAiFollowup,
} from './aiFollowupApi';
import { useAiProviderSetting } from './aiProviderApi';
import { useTelegramSetting } from './telegramApi';
import { getApiErrorMessage } from '../../lib/api-client';

export function AiFollowupForm(): JSX.Element {
  // 試跑後用 enqueuedAt 觸發短期輪詢,直到 lastRun.runAt 超過它
  const [enqueuedAt, setEnqueuedAt] = useState<number | null>(null);
  const { data, isLoading, isError, error } = useAiFollowupSetting({
    pollUntilNewRunAfter: enqueuedAt,
  });
  const { data: provider } = useAiProviderSetting();
  const { data: telegram } = useTelegramSetting();
  const updateMutation = useUpdateAiFollowup();
  const runMutation = useRunAiFollowupNow();
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  // 背景 job 跑完(lastRun.runAt 超過 enqueuedAt)→ 停止輪詢
  useEffect(() => {
    if (enqueuedAt == null) return;
    const runAt = data?.lastRun?.runAt ? new Date(data.lastRun.runAt).getTime() : 0;
    if (runAt > enqueuedAt) setEnqueuedAt(null);
  }, [enqueuedAt, data?.lastRun?.runAt]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<AiFollowupSettingInput>({
    resolver: zodResolver(AiFollowupSettingInputSchema),
    defaultValues: {
      enabled: false,
      notifyTime: '09:00',
      timezone: 'Asia/Taipei',
      staleDays: 7,
      interviewTypeIds: [],
      maxItems: 10,
      promptTemplate: '',
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        enabled: data.enabled,
        notifyTime: data.notifyTime,
        timezone: data.timezone,
        staleDays: data.staleDays,
        interviewTypeIds: data.interviewTypeIds,
        maxItems: data.maxItems,
        promptTemplate: data.promptTemplate,
      });
    }
  }, [data, reset]);

  const enabledWatched = watch('enabled');
  const providerReady = Boolean(provider?.enabled && provider?.hasApiKey && provider?.baseUrl);
  const telegramReady = Boolean(telegram?.enabled && telegram?.hasBotToken && telegram?.chatId);
  const canEnable = providerReady && telegramReady;

  if (isLoading) return <div role="status" className="text-slate-500">載入中...</div>;
  if (isError) {
    return (
      <div role="alert" className="text-red-600">
        載入失敗：{getApiErrorMessage(error, '未知錯誤')}
      </div>
    );
  }

  const onSubmit = (values: AiFollowupSettingInput): void => {
    setSavedFlash(null);
    updateMutation.mutate(values, {
      onSuccess: () => setSavedFlash('已儲存'),
    });
  };

  const errMsg = (e: unknown): string => getApiErrorMessage(e, '未知錯誤');

  return (
    <section className="grid gap-4 max-w-2xl">
      <h2 className="text-lg font-semibold">AI 每日追蹤代辦</h2>
      <p className="text-sm text-slate-600">
        於每日設定時間,將所有開啟「🔔 提醒」的訪談紀錄推播到 Telegram(AI 針對每筆產出建議文字)。
        在客戶操作台的訪談紀錄上點「🔕 提醒 / 🔔 提醒中」按鈕切換。
      </p>

      {!canEnable ? (
        <div role="alert" className="rounded border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm">
          需先完成下列設定才可啟用：
          <ul className="list-disc ml-5 mt-1">
            {!providerReady ? <li>AI 提供商（base URL + API Key + 啟用）</li> : null}
            {!telegramReady ? <li>Telegram（Bot Token + Chat ID + 啟用）</li> : null}
          </ul>
        </div>
      ) : null}

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3 bg-white border rounded p-4">
        <label className="inline-flex items-center gap-2 text-sm" title={canEnable ? '' : '需先完成 AI 提供商與 Telegram 設定'}>
          <input
            type="checkbox"
            {...register('enabled')}
            disabled={!canEnable}
          /> 啟用每日推播
        </label>

        <div className="grid grid-cols-2 gap-3">
          <Row label="通知時間（HH:mm）" error={errors.notifyTime?.message}>
            <input type="time" {...register('notifyTime')} className={inputCls} />
          </Row>
          <Row label="時區" error={errors.timezone?.message}>
            <select {...register('timezone')} className={inputCls}>
              {AI_FOLLOWUP_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </Row>
        </div>

        {/* 以下三欄(最近 N 天 / 每日最多筆數 / 涵蓋類別)為舊版條件,
            現在改以訪談項目上的「🔔 提醒」開關取代;保留欄位只是避免 schema 破壞。*/}
        <input type="hidden" {...register('staleDays', { valueAsNumber: true })} />
        <input type="hidden" {...register('maxItems', { valueAsNumber: true })} />
        <Controller
          control={control}
          name="interviewTypeIds"
          render={({ field }) => (
            <input type="hidden" value={JSON.stringify(field.value)} onChange={() => undefined} />
          )}
        />

        <Row label="Prompt 模板（留空使用預設）" error={errors.promptTemplate?.message}>
          <textarea
            {...register('promptTemplate')}
            className={`${inputCls} font-mono text-xs`}
            rows={10}
            placeholder={AI_FOLLOWUP_DEFAULT_PROMPT}
          />
        </Row>

        {updateMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">儲存失敗：{errMsg(updateMutation.error)}</div>
        ) : null}
        {savedFlash ? (
          <div role="status" className="text-green-700 text-sm">{savedFlash}</div>
        ) : null}

        <div className="flex gap-2 items-center">
          <button
            type="submit"
            disabled={updateMutation.isPending || (enabledWatched && !canEnable)}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? '儲存中…' : '儲存'}
          </button>
          <button
            type="button"
            disabled={runMutation.isPending || !canEnable}
            title={canEnable ? '不等排程,立即跑一次並推播 Telegram' : '需先完成 AI 提供商與 Telegram 設定'}
            onClick={() =>
              runMutation.mutate(undefined, {
                onSuccess: () => setEnqueuedAt(Date.now()),
              })
            }
            className="border border-blue-600 text-blue-600 px-3 py-1.5 rounded hover:bg-blue-50 disabled:opacity-50"
          >
            {runMutation.isPending ? '排程中…' : '立即試跑'}
          </button>
        </div>

        {enqueuedAt != null ? (
          <div role="status" className="text-blue-700 text-sm">
            ⏳ 試跑執行中(背景 job 約需 10–30 秒)…結果會自動更新於下方「上次執行」,無需重新整理。
          </div>
        ) : runMutation.isSuccess ? (
          <div role="status" className="text-green-700 text-sm">
            ✓ 試跑完成,見下方「上次執行」。
          </div>
        ) : null}
        {runMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">
            排程失敗：{errMsg(runMutation.error)}
          </div>
        ) : null}
      </form>

      {data?.lastRun ? (
        <section className="bg-white border rounded p-4 text-sm grid gap-1">
          <h3 className="font-semibold">上次執行</h3>
          <div>時間：{new Date(data.lastRun.runAt).toLocaleString()}</div>
          <div>狀態：<StatusBadge status={data.lastRun.status} /> {data.lastRun.manual ? '(手動試跑)' : '(排程)'}</div>
          <div>候選 / 推播：{data.lastRun.candidates} / {data.lastRun.picked} 筆</div>
          <div>tokens in / out：{data.lastRun.tokensIn} / {data.lastRun.tokensOut}</div>
          <div>估算成本：USD {data.lastRun.costUsd.toFixed(4)}</div>
          {data.lastRun.error ? <div className="text-red-600">錯誤:{data.lastRun.error}</div> : null}
          {data.lastRun.messagePreview ? (
            <details className="mt-2">
              <summary className="cursor-pointer text-blue-700 hover:underline">
                查看發送內容
              </summary>
              <pre className="mt-2 whitespace-pre-wrap text-xs bg-slate-50 border rounded p-2 text-slate-800">
                {data.lastRun.messagePreview}
              </pre>
            </details>
          ) : null}
        </section>
      ) : null}
    </section>
  );
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const ok = status === 'ok';
  const cls = ok
    ? 'bg-green-100 text-green-800'
    : status === 'no_candidates'
      ? 'bg-slate-100 text-slate-700'
      : 'bg-amber-100 text-amber-800';
  return <span className={`inline-block px-2 py-0.5 rounded text-xs ${cls}`}>{status}</span>;
}

function Row({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error: string | undefined;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-slate-700">{label}</span>
      {children}
      {hint ? <span className="text-slate-500 text-xs">{hint}</span> : null}
      {error ? <span role="alert" className="text-red-600 text-xs">{error}</span> : null}
    </label>
  );
}

const inputCls = 'border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';
